import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { Box, Checkbox, Chip, Typography, alpha } from "@mui/material";
import type React from "react";
import { useTranslation } from "react-i18next";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import { formatRupiah } from "../../utils/formatRupiah";

type TranslationKeys = keyof typeof translations.en;

export interface ExpenseChecklistItem {
  key: string;
  label: string;
  amount?: number;
  overrideAmount?: number;
  detail?: string;
  isAdditional?: boolean;
  checked: boolean;
  needsRevision: boolean;
}

export type ExpenseChecklistReviewState = "needs-revision" | "override" | "checked" | "unchecked";

export const getExpenseChecklistItemReviewState = (
  item: Pick<ExpenseChecklistItem, "checked" | "needsRevision" | "overrideAmount">,
): ExpenseChecklistReviewState => {
  if (item.needsRevision) {
    return "needs-revision";
  }
  if (item.overrideAmount !== undefined && item.overrideAmount !== null) {
    return "override";
  }
  if (item.checked) {
    return "checked";
  }
  return "unchecked";
};

interface ExpenseChecklistProps {
  orderId: string;
  items: ExpenseChecklistItem[];
  section?: "all" | "standard" | "additional";
  columns?: 1 | 2;
  onToggle?: (orderId: string, itemKey: string, checked: boolean) => void;
  disabled?: boolean;
  disabledHint?: string;
  loading?: boolean;
}

const ExpenseChecklist: React.FC<ExpenseChecklistProps> = ({
  orderId,
  items,
  section = "all",
  columns = 1,
  onToggle,
  disabled = false,
  disabledHint,
  loading = false,
}) => {
  const { t } = useTranslation();

  if (section === "all" && items.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t("reports.noChecklistItems" as TranslationKeys)}
      </Typography>
    );
  }

  const standardItems = items.filter((item) => !item.isAdditional);
  const additionalItems = items.filter((item) => item.isAdditional);
  const listItems = section === "additional" ? additionalItems : standardItems;
  const chipItems =
    section === "all" ? items : section === "standard" ? standardItems : additionalItems;

  const panelTitle =
    section === "standard"
      ? t("reports.checklistRegular" as TranslationKeys)
      : section === "additional"
        ? t("orders.additionalExpenses" as TranslationKeys)
        : t("reports.checklist" as TranslationKeys);

  const checkedCount = chipItems.filter((item) => item.checked).length;
  const allChecked = chipItems.length > 0 && checkedCount === chipItems.length;

  const panelTestId =
    section === "standard"
      ? TestId.UJR.CHECKLIST_REGULAR
      : section === "additional"
        ? TestId.UJR.CHECKLIST_ADDITIONAL
        : TestId.UJR.CHECKLIST;

  const renderItem = (item: ExpenseChecklistItem) => {
    const isChecked = item.checked;
    const needsRevision = item.needsRevision;
    const hasOverride = item.overrideAmount !== undefined && item.overrideAmount !== null;
    const reviewState = getExpenseChecklistItemReviewState(item);

    return (
      <Box
        key={item.key}
        data-review-state={reviewState}
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          alignItems: "center",
          gap: 1,
          py: 0.5,
          px: 0.75,
          borderRadius: 1,
          border: hasOverride ? 1 : 0,
          borderColor: hasOverride ? "warning.main" : "transparent",
          bgcolor: needsRevision
            ? alpha("#F59E0B", 0.12)
            : hasOverride
              ? alpha("#F59E0B", 0.08)
              : isChecked
                ? alpha("#22C55E", 0.08)
                : "transparent",
          transition: "background-color 0.15s ease",
        }}
      >
        <Checkbox
          size="small"
          checked={isChecked}
          disabled={disabled || loading}
          onChange={() => onToggle?.(orderId, item.key, !isChecked)}
          icon={<RadioButtonUncheckedIcon fontSize="small" />}
          checkedIcon={<CheckCircleIcon fontSize="small" sx={{ color: "#22C55E" }} />}
          sx={{ p: 0.25 }}
          data-testid={TestId.UJR.CHECKLIST_ITEM(orderId, item.key)}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            {needsRevision && (
              <ErrorOutlineIcon fontSize="small" color="warning" sx={{ flexShrink: 0 }} />
            )}
            <Typography
              variant="body2"
              sx={{
                fontWeight: isChecked || needsRevision || hasOverride ? 600 : 400,
                color: needsRevision ? "warning.dark" : isChecked ? "#15803D" : "text.primary",
                lineHeight: 1.3,
              }}
            >
              {item.label}
            </Typography>
          </Box>
          {item.detail && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.3, pl: needsRevision ? 2.5 : 0 }}
            >
              {item.detail}
            </Typography>
          )}
        </Box>
        {item.amount !== undefined && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.75,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: hasOverride ? 400 : 600,
                fontVariantNumeric: "tabular-nums",
                textDecoration: hasOverride ? "line-through" : "none",
                color: hasOverride
                  ? "text.secondary"
                  : needsRevision
                    ? "warning.dark"
                    : isChecked
                      ? "#15803D"
                      : "text.secondary",
              }}
            >
              {formatRupiah(item.amount)}
            </Typography>
            {hasOverride && (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  color: "warning.dark",
                }}
              >
                {formatRupiah(item.overrideAmount)}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderEmptyCaption = () => {
    if (section === "additional") {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", px: 0.5, py: 0.25 }}
        >
          {t("orders.noAdditionalExpenses" as TranslationKeys)}
        </Typography>
      );
    }

    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", px: 0.5, py: 0.25 }}
      >
        {t("reports.noChecklistItems" as TranslationKeys)}
      </Typography>
    );
  };

  const renderItemGrid = () => {
    if (listItems.length === 0) {
      return renderEmptyCaption();
    }

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: columns === 2 ? { xs: "1fr", lg: "1fr 1fr" } : "1fr",
          columnGap: 1.5,
          rowGap: 0.25,
          alignItems: "start",
        }}
      >
        {listItems.map((item) => renderItem(item))}

        {section === "all" && (
          <>
            <Box
              sx={{
                gridColumn: "1 / -1",
                borderTop: 1,
                borderColor: "divider",
                mt: 0.5,
                pt: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ display: "block", px: 0.5 }}
              >
                {t("orders.additionalExpenses" as TranslationKeys)}
              </Typography>
            </Box>
            {additionalItems.length > 0 ? (
              additionalItems.map((item) => renderItem(item))
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ gridColumn: "1 / -1", px: 0.5, py: 0.25 }}
              >
                {t("orders.noAdditionalExpenses" as TranslationKeys)}
              </Typography>
            )}
          </>
        )}
      </Box>
    );
  };

  return (
    <Box
      data-testid={panelTestId}
      sx={{
        width: "100%",
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 1.25,
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.75,
          gap: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {panelTitle}
        </Typography>
        {chipItems.length > 0 ? (
          <Chip
            size="small"
            label={t("reports.checklistVerifiedCount" as TranslationKeys, {
              checked: checkedCount,
              total: chipItems.length,
            })}
            sx={{
              height: 22,
              fontWeight: 600,
              fontSize: "0.7rem",
              bgcolor: allChecked ? alpha("#22C55E", 0.15) : alpha("#94A3B8", 0.16),
              color: allChecked ? "#15803D" : "text.secondary",
            }}
          />
        ) : null}
      </Box>

      {renderItemGrid()}

      {section !== "additional" && disabled && disabledHint ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {disabledHint}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ExpenseChecklist;
